import { useCallback, useEffect, useMemo, useRef } from 'react'

import { FAVORITES } from '@pages/Trade/utils/constants'

import type { WatchlistModel } from '@model/DxTrade.types'

import { normalizeSymbols } from '../helpers'
import { useCreateWatchlist } from './useCreateWatchlist'
import { useUpdateWatchlist } from './useUpdateWatchlist'

export const useFavoritesWatchlist = (
  externalId?: string,
  privateWatchlists?: WatchlistModel[]
) => {
  const { mutate: createWatchlist } = useCreateWatchlist()
  const { mutate: updateWatchlist } = useUpdateWatchlist()

  const favoriteWatchlist = useMemo(
    () => privateWatchlists?.find((watchlist) => watchlist.name === FAVORITES),
    [privateWatchlists]
  )

  const favoriteSymbols = useMemo(
    () =>
      (favoriteWatchlist?.symbols ?? []).filter(
        (symbol): symbol is string => symbol != null && symbol.trim() !== ''
      ),
    [favoriteWatchlist?.symbols]
  )

  const symbolsRef = useRef(favoriteSymbols)

  useEffect(() => {
    symbolsRef.current = favoriteSymbols
  }, [favoriteSymbols])

  const handleToggleFavorite = useCallback(
    (symbol: string) => {
      const normalizedSymbol = symbol.trim()
      if (!normalizedSymbol || !externalId) return

      if (!favoriteWatchlist) {
        createWatchlist({
          name: FAVORITES,
          description: '',
          type: 'PRIVATE',
          kind: 'REGULAR',
          symbols: [normalizedSymbol],
          owner: {
            login: externalId,
            domain: 'default',
          },
        })
        return
      }

      const currentSymbols = normalizeSymbols(symbolsRef.current)
      const isFavorite = currentSymbols.includes(normalizedSymbol)
      const nextSymbols = isFavorite
        ? currentSymbols.filter((item) => item !== normalizedSymbol)
        : [...currentSymbols, normalizedSymbol]
      const sanitizedSymbols = normalizeSymbols(nextSymbols)

      symbolsRef.current = sanitizedSymbols

      updateWatchlist({
        id: favoriteWatchlist.id,
        payload: { symbols: sanitizedSymbols },
      })
    },
    [externalId, createWatchlist, favoriteWatchlist, updateWatchlist]
  )

  return {
    favoriteSymbols,
    handleToggleFavorite,
  }
}
